import React from 'react';
import { Clock, Coins, Brain, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';


function Home() {
  const navigate = useNavigate();
  const challenges = [
    {
      icon: <Clock className="w-6 h-6 text-pink-400" />,
      title: "Time is Always Running Out",
      description: "Juggling multiple tasks while maintaining quality feels impossible. From client management to actual work delivery, 24 hours never seems enough."
    },
    {
      icon: <Coins className="w-6 h-6 text-pink-400" />,
      title: "Limited Budget, Unlimited Needs",
      description: "Starting a business or freelancing career requires wearing many hats and specializing for every role - from marketing to delivery while staying cost-effective."
    },
    {
      icon: <Brain className="w-6 h-6 text-pink-400" />,
      title: "Mental Bandwidth Overload",
      description: "Constantly switching between different types of tasks drains your energy and creativity. Quality suffers when you're stretched too thin."
    },
    {
      icon: <User className="w-6 h-6 text-pink-400" />,
      title: "Flying Solo is Tough",
      description: "Working alone means handling everything yourself - from client communication to project management, marketing, and delivery."
    }
  ];

  return (
    <main className="min-h-screen">
      {/* Hero Section */}
      <section className="flex flex-col items-center">
        <div className="flex justify-center">
          <img 
            src="/image/hero-section.png" 
            alt="Home Banner" 
            className="w-40 h-40 sm:w-60 sm:h-60 md:w-80 md:h-80 mb-5"
          />
        </div>
        
        <div className="text-center w-full">
          <h1 className="text-4xl font-bold font-custom text-white mb-2">
            Your Personal AI <span className="text-pink-500">Sidekick</span>
          </h1>
          <p className="text-white mb-6">
            Your AI-powered sidekick adapts to your unique business requirements across multiple disciplines.
          </p>
          <button 
            onClick={() => navigate('/feature')} 
            className="inline-block bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-6 rounded-md transition-colors"
          >
            Get Started for Free
          </button>
        </div>
      </section>

      {/* AI Partners Section */}
      <section className="bg-white bg-opacity-25 -mx-10 md:-mx-20 pt-6 p-10 mt-16">
        <h2 className="text-center text-2xl mb-2 text-white">
          Unleash the Power of
        </h2>
        <div className="grid md:grid-cols-4 gap-8 pl-16 mb-2 items-center">
          <img src="/image/chatgpt.png" alt="ChatGPT" className="w-max h-36" />
          <img src="/image/gemini.png" alt="Gemini" className="w-max h-12" />
          <img src="/image/claude.png" alt="Claude" className="w-max h-24" />
          <img src="/image/llama.png" alt="LLaMA" className="w-max h-12" />
        </div>
      </section>

       {/* Features Section */}
      <section className="container mx-auto px-4 mt-10 mb-10">
            <h2 className="text-3xl font-semibold mb-8 text-white text-center">
              Build Your Sidekicks in One Click
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <FeatureCard 
                title="Smart Assistance"
                description="From marketing strategies to administrative tasks, operations management to creative design - your AI companion adapts to every business function."
              />
              <FeatureCard 
                title="Personalized AI Training"
                description="Train your Sidekick to understand your specific Standard Operating Procedures, business profile, and industry requirements for optimal results."
              />
              <FeatureCard 
                title="Secure & Private"
                description="Your data is encrypted and protected at all times."
              />
            </div>
      </section>

      {/* Modern Work Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-4xl font-bold text-center text-white mb-4">
          The Reality of Modern Work
        </h2>
        <p className="text-center text-gray-300 mb-12 max-w-2xl mx-auto">
          Whether you're starting a business, freelancing, or managing multiple 
          projects, these challenges might feel all too familiar...
        </p>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Image Section */}
          <div className="bg-cyan-400 rounded-lg overflow-hidden">
            <img
              src="/image/problem.jpg"
              alt="Professional working with tablet"
              className="w-full object-cover"
            />
          </div>

          {/* Challenges List */}
          <div className="space-y-8">
            {challenges.map((challenge, index) => (
              <div key={index} className="flex gap-4 items-start">
                <div className="mt-1">
                  {challenge.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-xl mb-2 text-white">
                    {challenge.title}
                  </h3>
                  <p className="text-gray-300 text-sm">
                    {challenge.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <button 
          onClick={() => navigate('/feature')}
          className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 px-8 rounded-md transition-colors">
            Try Sidekick for Help
          </button>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">
            What If You Could Overcome These<br />
            Challenges Without Breaking The Bank?
          </h2>
          <p className="text-gray-300">
            Explore the endless possibilities with Sidekick AI
          </p>
        </div>

        {/* Stats Container */}
        <div className="bg-gray-200 rounded-t-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 text-center py-8 px-4 gap-8 md:gap-4">
            <div className="flex flex-col items-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-2 text-blue-600">$15,000+</h3>
              <p className="text-gray-600 text-sm md:text-base">Monthly Cost Saved</p>
            </div>
            <div className="flex flex-col items-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-2 text-blue-600">60+hrs/month</h3>
              <p className="text-gray-600 text-sm md:text-base">Time Recovered</p>
            </div>
            <div className="flex flex-col items-center">
              <h3 className="text-3xl md:text-4xl font-bold mb-2 text-blue-600">100+</h3>
              <p className="text-gray-600 text-sm md:text-base">Tasks Automated</p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="bg-gray-200 rounded-b-lg p-8">
          <div className="grid md:grid-cols-2 gap-8 ml-4">
            {/* Left Column */}
            <div className="ml-4">
              <h3 className="text-xl font-bold mb-4">Your Intelligent Business Companion</h3>
              <p className="text-gray-600 mb-4">
                Sidekick learns your business inside out - from your processes to your brand voice, becoming a true extension of your work.
              </p>
              <ul className="space-y-3 text-gray-600">
                {[
                  "Adapts to your specific industry needs",
                  "Learns from your feedback and corrections",
                  "Maintains consistency across all tasks",
                  "Parallel task processing",
                  "Instant response capabilities"
                ].map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right Column */}
            <div className="ml-4">
              <h3 className="text-xl font-bold mb-4">Multi-Skilled Professional Sidekick</h3>
              <p className="text-gray-600 mb-4">
                Get expertise across multiple disciplines without the cost of hiring specialists.
              </p>
              <ul className="space-y-3 text-gray-600">
                {[
                  "Marketing & content creation",
                  "Administrative task automation",
                  "Content Writer",
                  "Business Analyst",
                  "Graphics Designer"
                ].map((skill, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    {skill}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="-mx-10 md:-mx-20 bg-customColor2">
        <div className="container mx-auto px-4 md:px-8 py-16 max-w-7xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">

          {/* Description */}
          <div className="space-y-8">
            <h3 className="font-bold text-3xl text-white">Get Started Right Now</h3>
            <p>Transform your work life with a customized AI assistant that understands your unique needs. Build a Sidekick AI that works alongside you, handling multiple tasks while you focus on what matters most. Whether you're a freelancer, entrepreneur, or professional, get the support you need to achieve more with less stress. Start your journey to enhanced productivity today.</p>
            <div className="text-center mt-20">
              <button
              onClick={() => navigate('/feature')} 
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3 px-8 rounded-md transition-colors items-start mt-6"
              >
                Try Sidekick for Help
              </button>
            </div>
          </div>

          {/* Image Section */}
          <div className="bg-cyan-400 rounded-lg overflow-hidden">
            <img
              src="/image/solution.jpg"
              alt="Professional working with tablet"
              className="w-full object-cover"
            />
          </div>
        </div>
        </div> 
      </section>

    </main>
  );
}

// Feature Card Component
const FeatureCard = ({ title, description }) => (
  <div className="bg-gray-100 p-6 rounded-lg shadow-md">
    <h3 className="text-2xl font-semibold mb-4">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

export default Home;